/**
 * Per-measure documentation links — used by the widget info-icon (Phase E1)
 * to deep-link to the relevant section of `docs/architecture/data-dictionary.md`.
 *
 * The `summary` field is shown inline in the modal body; the `dictionarySection`
 * is the anchor in the data dictionary the modal links to. The dictionary file
 * lives in the repo and is rendered statically (no runtime fetch).
 */
export interface MeasureDoc {
  /** Short human-readable summary of what the measure represents. */
  summary: string
  /** Anchor in `docs/architecture/data-dictionary.md`. Without leading #. */
  dictionarySection: string
  /** Caveats / known data quality issues for the leader. */
  caveats?: string[]
  /** Whether this measure is FAR-only (informs CoverageBadge rendering). */
  farOnly?: boolean
}

export const MEASURE_DOCS: Record<string, MeasureDoc> = {
  office_performance: {
    summary: 'Award counts and obligations rolled up per office × FY × FQ × PSC L1 × color of money × vehicle category.',
    dictionarySection: 'office_performance',
    caveats: ['`award_count` is per-action (mods + initials). Use `unique_award_count` for distinct contracts.'],
  },
  competition_health: {
    summary: 'Full-and-open share, single-bid rate, and average offers received per slice.',
    dictionarySection: 'competition_health',
    farOnly: true,
    caveats: [
      'FAR-only metric — OTs excluded from the denominator by design.',
      'Single-bid rate uses `awards_with_offer_data` (some FAR rows lack offer data).',
    ],
  },
  small_biz_engagement: {
    summary: 'Small-business award counts and dollar shares (8(a), HUBZone, SDVOSB, WOSB) per slice.',
    dictionarySection: 'small_biz_engagement',
    farOnly: true,
    caveats: [
      'FAR-only; SB rates are computed over `far_award_count`.',
      'SB indicator is the contracting officer\'s determination, not the obsolete SBCDP field.',
      'Set-aside count excludes literal `\'NONE\'`.',
    ],
  },
  contract_type_trends: {
    summary: 'Vehicle category mix (GWAC / IDIQ / BPA / OT / Definitive / etc.) over time.',
    dictionarySection: 'contract_type_trends',
  },
  ot_prevalence: {
    summary: 'Other Transaction adoption — share of awards by FAR vs OT.',
    dictionarySection: 'ot_prevalence',
    caveats: ['OTs biased low in early FYs until SAM ingestion catches up.'],
  },
  acq_performance: {
    summary: 'Cross-cut award activity and obligations across hierarchy levels.',
    dictionarySection: 'acq_performance',
  },
  activity_by_vertical: {
    summary: 'Spend grouped by PSC Level 1 (product/service category).',
    dictionarySection: 'activity_by_vertical',
    caveats: ['April 2024 PSC IT-services restructure end-dated 68 codes / added 40.'],
  },
  speed_to_contract: {
    summary: "Days from the SAM.gov award record's own solicitation date to award signature (date_signed). Reported as percentiles (p25/p50/p75/p90).",
    dictionarySection: 'speed_to_contract',
    caveats: [
      'Scope: base awards of types that record a real solicitation event — definitive contracts, IDCs, purchase orders, and OT agreements/IDVs/orders. Delivery orders and BPA calls are excluded (placed against an existing vehicle, no measurable lead time).',
      'FAR (is_ot=false) and Other Transactions (is_ot=true) are measured identically from the same source and ARE directly comparable.',
      'Percentiles are non-aggregable — office-level p50 cannot be rolled up to a portfolio p50 by simple averaging.',
    ],
  },
  contract_duration: {
    summary: 'Period-of-performance duration percentiles per office × FY × vehicle × pricing.',
    dictionarySection: 'contract_duration',
  },
  vendor_concentration: {
    summary: 'Per-vendor dollar share, rank, and cumulative share (Pareto) per office × FY.',
    dictionarySection: 'vendor_concentration',
    caveats: [
      'Cumulative share legitimately exceeds 1.0 mid-partition due to deobligations. Only the terminal row hits 1.000±0.001.',
      'Currently keyed on `recipient_uei` (subsidiary). Parent rollup via `?rollup=parent` lands with backend M.I2.',
    ],
  },
  fy_end_surge: {
    summary: 'Monthly spend distribution within each FY — Q4 concentration detection.',
    dictionarySection: 'fy_end_surge',
  },
  geo_performance: {
    summary: 'Geographic spend rolled up by state, congressional district, and PSC.',
    dictionarySection: 'geo_performance',
    caveats: ['Two narratives via `location_kind`: `recipient` (vendor HQ / DIB lens) vs `pop` (place of performance / workforce lens).'],
  },
}

/** Get the docs for a measure, falling back to a minimal stub. */
export function getMeasureDoc(measure: string): MeasureDoc {
  return MEASURE_DOCS[measure] ?? {
    summary: `Measure: ${measure}`,
    dictionarySection: '',
  }
}
